import { memo, useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';
import {
  Message,
  TaskSkill,
  MessageType,
  ErrorPayload,
  isMessageWithCreationTaskId,
  MessageTabs,
} from 'src/types';
import {
  useCurrentConversationTasks,
  useLeftPanelState,
  useBreakpoint,
  useComparingConversationsParams,
  useReadMessageByScrolling,
} from 'src/hooks';
import { MessageAuthorPicture } from '../MessageAuthorPicture';
import { ThreadMessageBody } from './components/ThreadMessageBody';
import { MessageProgress } from './components/MessageProgress';
import styles from './ThreadMessage.module.scss';
import { BlockedTaskMessage } from './components/BlockedTaskMessage';
import { MockedVideoProgress } from './components/MockedVideoProgress';
import { MessageActions } from './components/MessageActions';
import className from 'classnames';
import { getSkillFromMessage, getTaskIdFromMessage } from 'src/utils';

interface ThreadMessageProps {
  message: Message;
  isLast: boolean;
}

const ThreadMessageComponent = ({ message, isLast }: ThreadMessageProps) => {
  const [messageToShow, setMessageToShow] = useState(message);
  const { getTaskFromConversationById } = useCurrentConversationTasks(
    message.conversation_id,
  );
  const { isLeftPanelExpanded } = useLeftPanelState();
  const { isDesktop } = useBreakpoint();
  const { isCompareMode } = useComparingConversationsParams();

  const readMessageAnchorRef = useRef<HTMLDivElement>(null);

  const taskId = useMemo(() => getTaskIdFromMessage(message), [message]);

  const { taskState, taskTitle, isTaskRequiresAttention } = useMemo(() => {
    // TODO(olha): we need to create a map
    const currentTask = getTaskFromConversationById(taskId);

    return {
      taskState: currentTask?.state,
      taskTitle: currentTask?.task_hash?.replace('#', '') || '',
      isTaskRequiresAttention: !!currentTask?.requires_attention,
    };
  }, [taskId, getTaskFromConversationById]);

  const readMessageAnchorId = useMemo(
    () => (isMessageWithCreationTaskId(message) ? message.payload.task_id : ''),
    [message],
  );

  useReadMessageByScrolling({
    anchorRef: readMessageAnchorRef,
    taskId: readMessageAnchorId,
    isTaskRequiresAttention,
  });

  const [activeTab, setActiveTab] = useState<string>(MessageTabs.NINJA_AGENT);

  const { message_id, is_final_answer } = message;

  const messageSkill = useMemo(() => getSkillFromMessage(message), [message]);

  const throttleMessage = useRef(
    throttle((newMessage: Message) => {
      setMessageToShow(newMessage);
    }, 500),
  );

  useEffect(() => {
    throttleMessage.current(message);
  }, [message]);

  useEffect(() => {
    if (is_final_answer) {
      setMessageToShow(message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_final_answer]);

  const { payload } = messageToShow;

  const messageProgressCustomComponent = (
    payload as ErrorPayload
  )?.unsupported_skills?.includes(TaskSkill.VIDEO) ? (
    <MockedVideoProgress />
  ) : undefined;

  if (!message_id) {
    return null;
  }

  return (
    <div
      className={className(styles.root, {
        [styles.smallWidth]: isLeftPanelExpanded && isDesktop,
        [styles.isTwoColumns]: isCompareMode,
        [styles.isTwoColumnsWithOpenedLeftPanel]:
          isCompareMode && isLeftPanelExpanded,
      })}
    >
      <div
        className={styles.header}
        id={readMessageAnchorId}
        ref={readMessageAnchorRef}
      >
        <MessageAuthorPicture
          role={messageToShow.role}
          canShowProgress={isLast}
        />

        <MessageActions
          message={message}
          isStreaming={!is_final_answer}
          taskId={taskId}
          taskState={taskState}
          messageSkill={messageSkill}
          activeTab={activeTab}
        />
      </div>

      <div className={styles.container}>
        <MessageProgress
          message={message}
          customComponent={messageProgressCustomComponent}
          taskId={taskId}
          taskState={taskState}
          messageSkill={messageSkill}
        />

        {messageToShow.message_type ===
          MessageType.SCHEDULER_TASK_CREATION_CARD && (
          <BlockedTaskMessage taskState={taskState} taskId={taskId} />
        )}

        <ThreadMessageBody
          messageId={message_id}
          isStreaming={!is_final_answer}
          message={message}
          messageSkill={messageSkill}
          taskTitle={taskTitle}
          isLast={isLast}
          setActiveTab={setActiveTab}
          taskState={taskState}
        />
      </div>
    </div>
  );
};

export const ThreadMessage = memo(
  ThreadMessageComponent,
  (prevProps, nextProps) => {
    return (
      isEqual(prevProps.message, nextProps.message) &&
      prevProps.isLast === nextProps.isLast
    );
  },
);
